import { useSession } from "@internal/auth";
import { useEffect, useState } from "react";
import { getDbForUser, type PlannerDoc } from "@/lib/db";
import { WEEK_TYPE, type WeekData } from "@/lib/schema";

interface WeekRecord {
  id: string;
  data: WeekData;
}

function sortByDate(a: WeekRecord, b: WeekRecord): number {
  if (a.data.year !== b.data.year) return b.data.year - a.data.year;
  return b.data.weekNumber - a.data.weekNumber;
}

function useWeeksByState(state: WeekData["state"] | null): {
  weeks: WeekRecord[];
  loading: boolean;
} {
  const { user } = useSession();
  const [result, setResult] = useState<{
    weeks: WeekRecord[];
    loading: boolean;
  }>({ weeks: [], loading: true });

  useEffect(() => {
    if (!user) {
      setResult({ weeks: [], loading: false });
      return;
    }
    let cancelled = false;
    let sub: { unsubscribe: () => void } | null = null;
    (async () => {
      const db = await getDbForUser(user.id);
      if (cancelled) return;
      sub = db.daily_planner
        .find({ selector: { user_id: user.id, type: WEEK_TYPE } })
        .$.subscribe((docs) => {
          const all = docs.map((d) => {
            const json = d.toJSON() as PlannerDoc;
            return { id: json.id, data: json.data as unknown as WeekData };
          });
          const filtered = all
            .filter((w) => state === null || w.data.state === state)
            .sort(sortByDate);
          setResult({ weeks: filtered, loading: false });
        });
    })();
    return () => {
      cancelled = true;
      sub?.unsubscribe();
    };
  }, [user, state]);

  return result;
}

/** Open and completed alike — each holds its ISO week. */
export function useAllWeeks() {
  return useWeeksByState(null);
}

export function useOpenWeeks() {
  return useWeeksByState("open");
}

export function useCompletedWeeks() {
  return useWeeksByState("completed");
}

export function useWeek(weekId: string | undefined): {
  week: WeekRecord | null;
  loading: boolean;
} {
  const { user } = useSession();
  const [state, setState] = useState<{
    week: WeekRecord | null;
    loading: boolean;
  }>({ week: null, loading: true });

  useEffect(() => {
    if (!user || !weekId) {
      setState({ week: null, loading: false });
      return;
    }
    let cancelled = false;
    let sub: { unsubscribe: () => void } | null = null;
    (async () => {
      const db = await getDbForUser(user.id);
      if (cancelled) return;
      sub = db.daily_planner.findOne(weekId).$.subscribe((doc) => {
        if (!doc) {
          setState({ week: null, loading: false });
          return;
        }
        const json = doc.toJSON() as PlannerDoc;
        setState({
          week: { id: json.id, data: json.data as unknown as WeekData },
          loading: false,
        });
      });
    })();
    return () => {
      cancelled = true;
      sub?.unsubscribe();
    };
  }, [user, weekId]);

  return state;
}

export function usePreviousWeek(
  year: number | null,
  weekNumber: number | null,
): { week: WeekRecord | null; loading: boolean } {
  const { user } = useSession();
  const [state, setState] = useState<{
    week: WeekRecord | null;
    loading: boolean;
  }>({ week: null, loading: true });

  useEffect(() => {
    if (!user || year === null || weekNumber === null) {
      setState({ week: null, loading: false });
      return;
    }
    let cancelled = false;
    let sub: { unsubscribe: () => void } | null = null;
    (async () => {
      const db = await getDbForUser(user.id);
      if (cancelled) return;
      sub = db.daily_planner
        .find({ selector: { user_id: user.id, type: WEEK_TYPE } })
        .$.subscribe((docs) => {
          const all = docs.map((d) => {
            const json = d.toJSON() as PlannerDoc;
            return { id: json.id, data: json.data as unknown as WeekData };
          });
          const match = all.find(
            (w) => w.data.year === year && w.data.weekNumber === weekNumber,
          );
          setState({ week: match ?? null, loading: false });
        });
    })();
    return () => {
      cancelled = true;
      sub?.unsubscribe();
    };
  }, [user, year, weekNumber]);

  return state;
}
